import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ERROR_MAP = {
  JAH_ERR_INVALID_CREDENTIALS: { ar: 'البريد الإلكتروني أو كلمة المرور غير صحيحة', en: 'Incorrect email or password' },
  JAH_ERR_AUTH_FAILED:         { ar: 'كلمة المرور أو اسم المستخدم غير صحيح',        en: 'Incorrect username or password' },
  JAH_ERR_ACCOUNT_LOCKED:      { ar: 'الحساب مقفل مؤقتاً، حاول بعد قليل',          en: 'Account temporarily locked' },
  JAH_ERR_DUPLICATE_EMAIL:     { ar: 'البريد الإلكتروني مسجل بالفعل',               en: 'Email already registered' },
  JAH_ERR_MISSING_FIELDS:           { ar: 'يرجى ملء جميع الحقول المطلوبة',               en: 'Please fill all required fields' },
  JAH_ERR_INVALID_RECOVERY_CODE:    { ar: 'رمز الاستعادة غير صحيح أو منتهي الصلاحية',   en: 'Invalid or expired recovery code' },
};

function Spinner() {
  return (
    <span style={{
      width: 18, height: 18,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'jahbath-spin 0.7s linear infinite',
      flexShrink: 0,
    }} />
  );
}

function Field({ label, type = 'text', value, onChange, placeholder, dir, hasError }) {
  const { T } = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 18 }}>
      {label && (
        <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 7, fontWeight: 500 }}>
          {label}
        </label>
      )}
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} dir={dir}
        style={{
          width: '100%', padding: '12px 16px',
          background:   hasError ? 'rgba(254,226,226,.5)' : focused ? 'rgba(255,255,255,.98)' : 'rgba(248,250,252,.9)',
          border:       hasError ? '1px solid #EF4444' : focused ? '1px solid #1D4ED8' : '1px solid rgba(0,0,0,.1)',
          borderRadius: 12, color: '#1E3A5F', fontSize: 15,
          outline: 'none', boxSizing: 'border-box',
          transition: 'border-color 0.2s ease, background 0.2s ease',
          fontFamily: 'inherit',
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </div>
  );
}

function LangButton({ active, onClick, children, T }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, padding: 10,
      border:      `1px solid ${active ? T.accent : T.border}`,
      borderRadius: 10,
      background:  active ? `${T.accent}20` : T.bgInput,
      color:       active ? T.accent : T.textSecondary,
      fontSize: 14, fontWeight: active ? 600 : 400,
      cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: 'inherit',
    }}>
      {children}
    </button>
  );
}

export default function GuardianAuth() {
  const navigate = useNavigate();
  const { T, setThemeMode } = useTheme();
  const { lang, setLang } = useGuardianLanguage();

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [screen,      setScreen]      = useState('login');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [fe,          setFe]          = useState({});
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [name,        setName]        = useState('');
  const [regEmail,    setRegEmail]    = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [reportLang,  setReportLang]  = useState('ar');
  const [forgotEmail,     setForgotEmail]     = useState('');
  const [recoveryCode,    setRecoveryCode]    = useState('');
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorCode,       setErrorCode]       = useState('');

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x3B82F6, transparent: true, opacity: 0.26, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x3B82F6, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x3B82F6, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const clear    = () => { setError(''); setSuccess(''); setFe({}); setErrorCode(''); };
  const switchTo = (s) => { clear(); setScreen(s); };

  const applyUiSettings = (ui) => {
    if (!ui) return;
    if (ui.theme && !localStorage.getItem('jahbath_theme')) setThemeMode(ui.theme);
    if (ui.lang  && !localStorage.getItem('jahbath_lang'))  setLang(ui.lang);
  };

  const parseError = (data) => {
    const code = data?.errors?.[0]?.code || '';
    const entry = ERROR_MAP[code];
    if (entry) return entry[lang] || entry.ar;
    return lang === 'ar'
      ? 'حدث خطأ غير متوقع، يرجى المحاولة مرة أخرى'
      : 'An unexpected error occurred, please try again';
  };

  const doLogin = async (username, pwd) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password: pwd, user_type: 'guardian' }),
    });
    return res.json();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!email.trim()) errs.email = true;
    if (!password)     errs.password = true;
    if (Object.keys(errs).length) { setFe(errs); return; }
    setLoading(true); clear();
    try {
      const data = await doLogin(email.trim(), password);
      if (data.status === 'success') {
        localStorage.setItem('jahbath_token',        data.payload.session_token);
        localStorage.setItem('jahbath_guardian_id',  data.payload.user_id);
        localStorage.setItem('jahbath_report_lang',  data.payload.language_preference || 'ar');
        applyUiSettings(data.payload.ui_settings);
        navigate('/guardian/dashboard');
      } else {
        const code = data?.errors?.[0]?.code || '';
        setErrorCode(code);
        setError(parseError(data));
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!name.trim())                            errs.name = true;
    if (!regEmail.trim())                        errs.regEmail = true;
    if (!regPassword || regPassword.length < 8)  errs.regPassword = true;
    if (Object.keys(errs).length) { setFe(errs); return; }
    setLoading(true); clear();
    try {
      const res = await fetch(`${API}/auth/register/guardian`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: name.trim(), email: regEmail.trim(), password: regPassword, report_lang: reportLang }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        const autoLogin = await doLogin(data.payload.username, regPassword);
        if (autoLogin.status === 'success') {
          localStorage.setItem('jahbath_token',        autoLogin.payload.session_token);
          localStorage.setItem('jahbath_guardian_id',  autoLogin.payload.user_id);
          localStorage.setItem('jahbath_report_lang',  autoLogin.payload.language_preference || reportLang || 'ar');
          applyUiSettings(autoLogin.payload.ui_settings);
          navigate('/guardian/dashboard');
        } else {
          setSuccess(lang === 'ar' ? 'تم إنشاء الحساب! يمكنك الآن تسجيل الدخول' : 'Account created! You can now log in');
          switchTo('login');
        }
      } else {
        setError(parseError(data));
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setFe({ forgotEmail: true }); return; }
    setLoading(true); clear();
    try {
      const res = await fetch(`${API}/auth/password-reset/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        switchTo('verify_code');
      } else {
        setError(parseError(data));
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!recoveryCode.trim()) { setFe({ recoveryCode: true }); return; }
    setLoading(true); clear();
    try {
      const res = await fetch(`${API}/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: recoveryCode.trim() }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        switchTo('reset_password');
      } else {
        setError(parseError(data));
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!newPassword || newPassword.length < 8) errs.newPassword = true;
    if (confirmPassword !== newPassword)         errs.confirmPassword = true;
    if (Object.keys(errs).length) { setFe(errs); return; }
    setLoading(true); clear();
    try {
      const res = await fetch(`${API}/auth/password-reset/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: recoveryCode.trim(), new_password: newPassword }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setSuccess(lang === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
        setTimeout(() => switchTo('login'), 2000);
      } else {
        setError(parseError(data));
      }
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const submitBtn = {
    width: '100%', padding: 13,
    background: T.accentGradient,
    border: 'none', borderRadius: 12, color: 'white',
    fontSize: 16, fontWeight: 700, cursor: 'pointer',
    transition: 'opacity 0.2s ease',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    marginBottom: 20, fontFamily: 'Sora, sans-serif',
  };

  const textBtn = {
    fontSize: 13, color: T.accent,
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 0, fontFamily: 'inherit', transition: 'color 0.2s ease',
  };

  return (
    <div style={{
      minHeight: '100vh', background: 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Nunito', sans-serif",
      direction: dir, position: 'relative', overflow: 'hidden', padding: 20,
      transition: 'background 0.2s ease',
    }}>
      <canvas ref={canvasRef} style={{position:'fixed',inset:0,zIndex:0,pointerEvents:'none'}} />
      <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 10 }}>
        <button
          type="button"
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
          style={{
            background: 'white',
            border: '1px solid #E5E7EB',
            color: '#059669',
            fontWeight: 700,
            fontSize: '0.8rem',
            padding: '4px 12px',
            borderRadius: 20,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {lang === 'ar' ? 'EN' : 'ع'}
        </button>
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;500;600;700;800&display=swap');
        @keyframes jahbath-spin { to { transform: rotate(360deg); } }
        *, *::before, *::after { box-sizing: border-box; }
      `}</style>

      <div style={{
        position: 'absolute', top: -200, right: -200,
        width: 600, height: 600, borderRadius: '50%',
        background: `radial-gradient(circle, ${T.orbColor1} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: -200, left: -200,
        width: 600, height: 600, borderRadius: '50%',
        background: `radial-gradient(circle, ${T.orbColor2} 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(${T.gridLine} 1px,transparent 1px),linear-gradient(90deg,${T.gridLine} 1px,transparent 1px)`,
        backgroundSize: '40px 40px', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 440 }}>
        <a
          href="/choose-role"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 13, color: T.accent, textDecoration: 'none',
            marginBottom: 12, opacity: 0.8,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0.8}
        >
          {lang === 'ar' ? '← الرئيسية' : '← Home'}
        </a>
      <div style={{
        background: 'rgba(255,255,255,.72)', backdropFilter: 'blur(32px) saturate(2.2)',
        WebkitBackdropFilter: 'blur(32px) saturate(2.2)',
        border: '1px solid rgba(255,255,255,.92)',
        borderRadius: 24, padding: '40px 36px',
        boxShadow: '0 8px 48px rgba(0,0,0,.07), 0 1px 0 rgba(255,255,255,.98) inset',
        transition: 'background 0.2s ease, border-color 0.2s ease',
      }}>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32, cursor: 'pointer' }}
          onClick={() => {
            const token = localStorage.getItem('jahbath_token');
            if (token) navigate('/guardian/dashboard');
            else navigate('/choose-role');
          }}
        >
          <img
            src="/jahbath-hat.png"
            alt="Jahbath"
            style={{ width: 56, height: 56, objectFit: 'contain', marginBottom: 8 }}
          />
          <span style={{ fontSize: 22, fontWeight: 800, fontFamily: 'Sora, sans-serif', color: '#1E3A5F', letterSpacing: 1 }}>
            {lang === 'ar' ? 'جهبذ' : 'Jahbath'}
          </span>
          <span style={{ fontSize: 11, color: '#9CA3AF', letterSpacing: 3, marginTop: 2 }}>JAHBATH</span>
        </div>

        {screen === 'login' && (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Sora',sans-serif", color: '#1E3A5F', textAlign: 'center', margin: '0 0 28px' }}>
              {lang === 'ar' ? 'مرحباً بك' : 'Welcome Back'}
            </h1>
            {error   && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}40`, borderRadius: 10, padding: '12px 16px', color: T.error, fontSize: 13, marginBottom: errorCode === 'JAH_ERR_ACCOUNT_LOCKED' ? 8 : 18, textAlign: 'center' }}>{error}</div>}
            {errorCode === 'JAH_ERR_ACCOUNT_LOCKED' && (
              <div style={{ textAlign: 'center', marginBottom: 18 }}>
                <button type="button" style={textBtn} onClick={() => switchTo('forgot')}>
                  {lang === 'ar' ? 'نسيت كلمة المرور؟ استعد حسابك' : 'Forgot password? Recover your account'}
                </button>
              </div>
            )}
            {success && <div style={{ background: `${T.success}18`, border: `1px solid ${T.success}40`, borderRadius: 10, padding: '12px 16px', color: T.success, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{success}</div>}
            <form onSubmit={handleLogin} noValidate>
              <Field label={lang === 'ar' ? 'البريد الإلكتروني' : 'Email'} type="email" value={email} onChange={setEmail} placeholder="example@email.com" dir="ltr" hasError={fe.email} />
              <Field label={lang === 'ar' ? 'كلمة المرور' : 'Password'} type="password" value={password} onChange={setPassword} placeholder="••••••••" hasError={fe.password} />
              <div style={{ textAlign: dir === 'rtl' ? 'left' : 'right', marginTop: -8, marginBottom: 22 }}>
                <button type="button" style={textBtn} onClick={() => switchTo('forgot')}>
                  {lang === 'ar' ? 'نسيت كلمة المرور؟' : 'Forgot password?'}
                </button>
              </div>
              <button type="submit" style={{ ...submitBtn, opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} disabled={loading}>
                {loading && <Spinner />}
                {loading ? (lang === 'ar' ? 'جارٍ الدخول...' : 'Signing in...') : (lang === 'ar' ? 'دخول' : 'Sign In')}
              </button>
            </form>
            <div style={{ height: 1, background: T.border, margin: '4px 0 20px' }} />
            <div style={{ textAlign: 'center', color: T.textSecondary, fontSize: 13 }}>
              {lang === 'ar' ? 'ليس لديك حساب؟ ' : "Don't have an account? "}
              <button style={textBtn} onClick={() => switchTo('register')}>
                {lang === 'ar' ? 'إنشاء حساب جديد' : 'Create account'}
              </button>
            </div>
          </>
        )}

        {screen === 'register' && (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 800, fontFamily: "'Sora',sans-serif", color: '#1E3A5F', textAlign: 'center', margin: '0 0 28px' }}>
              {lang === 'ar' ? 'إنشاء حساب' : 'Create Account'}
            </h1>
            {error && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}40`, borderRadius: 10, padding: '12px 16px', color: T.error, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{error}</div>}
            <form onSubmit={handleRegister} noValidate>
              <Field label={lang === 'ar' ? 'الاسم الكامل' : 'Full Name'} value={name} onChange={setName} placeholder={lang === 'ar' ? 'أحمد محمد' : 'John Smith'} hasError={fe.name} />
              <Field label={lang === 'ar' ? 'البريد الإلكتروني' : 'Email'} type="email" value={regEmail} onChange={setRegEmail} placeholder="example@email.com" dir="ltr" hasError={fe.regEmail} />
              <Field label={lang === 'ar' ? 'كلمة المرور (8 أحرف على الأقل)' : 'Password (min 8 characters)'} type="password" value={regPassword} onChange={setRegPassword} placeholder="••••••••" hasError={fe.regPassword} />
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, color: T.textSecondary, marginBottom: 7, fontWeight: 500 }}>
                  {lang === 'ar' ? 'لغة التقارير' : 'Report Language'}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <LangButton active={reportLang === 'ar'} onClick={() => setReportLang('ar')} T={T}>العربية</LangButton>
                  <LangButton active={reportLang === 'en'} onClick={() => setReportLang('en')} T={T}>English</LangButton>
                </div>
              </div>
              <button type="submit" style={{ ...submitBtn, opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} disabled={loading}>
                {loading && <Spinner />}
                {loading ? (lang === 'ar' ? 'جارٍ الإنشاء...' : 'Creating...') : (lang === 'ar' ? 'إنشاء الحساب' : 'Create Account')}
              </button>
            </form>
            <div style={{ height: 1, background: T.border, margin: '4px 0 20px' }} />
            <div style={{ textAlign: 'center', color: T.textSecondary, fontSize: 13 }}>
              {lang === 'ar' ? 'لديك حساب بالفعل؟ ' : 'Already have an account? '}
              <button style={textBtn} onClick={() => switchTo('login')}>
                {lang === 'ar' ? 'تسجيل الدخول' : 'Sign In'}
              </button>
            </div>
          </>
        )}

        {screen === 'forgot' && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Sora',sans-serif", color: '#1E3A5F', textAlign: 'center', margin: '0 0 28px' }}>
              {lang === 'ar' ? 'استعادة كلمة المرور' : 'Reset Password'}
            </h1>
            {error && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}40`, borderRadius: 10, padding: '12px 16px', color: T.error, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{error}</div>}
            <form onSubmit={handleForgot} noValidate>
              <Field label={lang === 'ar' ? 'البريد الإلكتروني' : 'Email'} type="email" value={forgotEmail} onChange={setForgotEmail} placeholder="example@email.com" dir="ltr" hasError={fe.forgotEmail} />
              <button type="submit" style={{ ...submitBtn, opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} disabled={loading}>
                {loading && <Spinner />}
                {loading
                  ? (lang === 'ar' ? 'جارٍ الإرسال...' : 'Sending...')
                  : (lang === 'ar' ? 'إرسال رمز الاستعادة' : 'Send Recovery Code')}
              </button>
            </form>
            <div style={{ height: 1, background: T.border, margin: '4px 0 20px' }} />
            <div style={{ textAlign: 'center', color: T.textSecondary, fontSize: 13 }}>
              <button style={textBtn} onClick={() => switchTo('login')}>
                {lang === 'ar' ? 'رجوع لتسجيل الدخول' : 'Back to Sign In'}
              </button>
            </div>
          </>
        )}

        {screen === 'verify_code' && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Sora',sans-serif", color: '#1E3A5F', textAlign: 'center', margin: '0 0 12px' }}>
              {lang === 'ar' ? 'أدخل رمز الاستعادة' : 'Enter Recovery Code'}
            </h1>
            <p style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', margin: '0 0 24px' }}>
              {lang === 'ar'
                ? `تم إرسال رمز إلى ${forgotEmail}`
                : `A code was sent to ${forgotEmail}`}
            </p>
            {error && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}40`, borderRadius: 10, padding: '12px 16px', color: T.error, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{error}</div>}
            <form onSubmit={handleVerify} noValidate>
              <Field label={lang === 'ar' ? 'رمز الاستعادة' : 'Recovery Code'} value={recoveryCode} onChange={setRecoveryCode} placeholder="XXXXXX" dir="ltr" hasError={fe.recoveryCode} />
              <button type="submit" style={{ ...submitBtn, opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} disabled={loading}>
                {loading && <Spinner />}
                {loading
                  ? (lang === 'ar' ? 'جارٍ التحقق...' : 'Verifying...')
                  : (lang === 'ar' ? 'تحقق من الرمز' : 'Verify Code')}
              </button>
            </form>
            <div style={{ height: 1, background: T.border, margin: '4px 0 20px' }} />
            <div style={{ textAlign: 'center', color: T.textSecondary, fontSize: 13 }}>
              <button style={textBtn} onClick={() => switchTo('forgot')}>
                {lang === 'ar' ? 'إعادة إرسال الرمز' : 'Resend code'}
              </button>
            </div>
          </>
        )}

        {screen === 'reset_password' && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Sora',sans-serif", color: '#1E3A5F', textAlign: 'center', margin: '0 0 28px' }}>
              {lang === 'ar' ? 'كلمة مرور جديدة' : 'New Password'}
            </h1>
            {error   && <div style={{ background: `${T.error}18`, border: `1px solid ${T.error}40`, borderRadius: 10, padding: '12px 16px', color: T.error, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{error}</div>}
            {success && <div style={{ background: `${T.success}18`, border: `1px solid ${T.success}40`, borderRadius: 10, padding: '12px 16px', color: T.success, fontSize: 13, marginBottom: 18, textAlign: 'center' }}>{success}</div>}
            {!success && (
              <form onSubmit={handleReset} noValidate>
                <Field label={lang === 'ar' ? 'كلمة المرور الجديدة (8 أحرف على الأقل)' : 'New Password (min 8 characters)'} type="password" value={newPassword} onChange={setNewPassword} placeholder="••••••••" hasError={fe.newPassword} />
                <Field label={lang === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'} type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="••••••••" hasError={fe.confirmPassword} />
                <button type="submit" style={{ ...submitBtn, opacity: loading ? 0.65 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} disabled={loading}>
                  {loading && <Spinner />}
                  {loading
                    ? (lang === 'ar' ? 'جارٍ الحفظ...' : 'Saving...')
                    : (lang === 'ar' ? 'حفظ كلمة المرور' : 'Save Password')}
                </button>
              </form>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
